import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FaClipboardList,
  FaPlus,
  FaTrash,
  FaEdit,
  FaCheckCircle,
  FaClock,
  FaEye,
  FaLock,
  FaUnlock,
  FaPlay,
  FaStop,
  FaExclamationTriangle,
  FaSave,
  FaList,
} from "react-icons/fa";
import { toast } from "react-toastify";
import {
  createAssessment,
  deleteAssessment as deleteAssessmentApi,
  listAssessments,
  publishAssessment as publishAssessmentApi,
  startAssessmentAttempt,
  submitAssessmentAttempt,
  unpublishAssessment as unpublishAssessmentApi,
  updateAssessment,
} from "../api/assessments";

// ─── constants ────────────────────────────────────────────────────────────────
const MARK_CATEGORIES = [
  { value: 1, label: "1 Mark", color: "bg-sky-100 text-sky-700 border-sky-200" },
  { value: 5, label: "5 Marks", color: "bg-violet-100 text-violet-700 border-violet-200" },
  { value: 10, label: "10 Marks", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: 20, label: "20 Marks", color: "bg-rose-100 text-rose-700 border-rose-200" },
];

const DEFAULT_QUESTION = { text: "", marks: 1, type: "short", options: ["", "", "", ""], answer: "" };

// ─── sub-component: Question editor card ──────────────────────────────────────
const QuestionEditor = ({ question, index, onChange, onRemove }) => {
  const cat = MARK_CATEGORIES.find((c) => c.value === question.marks) || MARK_CATEGORIES[0];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${cat.color}`}>
          {cat.label}
        </span>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="rounded-full p-1.5 text-rose-400 hover:bg-rose-50 hover:text-rose-600"
        >
          <FaTrash size={12} />
        </button>
      </div>

      <div className="mt-3 space-y-2">
        {/* Mark picker */}
        <div className="flex flex-wrap gap-1.5">
          {MARK_CATEGORIES.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => onChange(index, "marks", c.value)}
              className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition ${
                question.marks === c.value ? c.color + " ring-2 ring-offset-1 ring-current" : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Type picker */}
        <div className="flex gap-2 text-xs font-semibold">
          {["short", "long", "mcq"].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onChange(index, "type", t)}
              className={`rounded-lg border px-3 py-1 transition ${
                question.type === t
                  ? "border-indigo-300 bg-indigo-100 text-indigo-700"
                  : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
              }`}
            >
              {t === "short" ? "Short answer" : t === "long" ? "Long answer" : "MCQ"}
            </button>
          ))}
        </div>

        {/* Question text */}
        <textarea
          rows={2}
          value={question.text}
          onChange={(e) => onChange(index, "text", e.target.value)}
          placeholder={`Q${index + 1}. Type your question here…`}
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
        />

        {/* MCQ options */}
        {question.type === "mcq" && (
          <div className="space-y-1.5 pl-2">
            {(question.options || ["", "", "", ""]).map((opt, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <span className="w-5 text-xs font-semibold text-slate-400">{String.fromCharCode(65 + oi)}.</span>
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => {
                    const next = [...(question.options || [])];
                    next[oi] = e.target.value;
                    onChange(index, "options", next);
                  }}
                  placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                  className="flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 placeholder-slate-400 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-100"
                />
                <input
                  type="radio"
                  name={`correct-${index}`}
                  checked={question.answer === opt && opt !== ""}
                  onChange={() => onChange(index, "answer", opt)}
                  title="Mark as correct answer"
                  className="accent-emerald-600"
                />
              </div>
            ))}
          </div>
        )}

        {/* Model answer for short/long */}
        {question.type !== "mcq" && (
          <input
            type="text"
            value={question.answer}
            onChange={(e) => onChange(index, "answer", e.target.value)}
            placeholder="Model / expected answer (for reference)"
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600 placeholder-slate-400 focus:border-indigo-300 focus:outline-none"
          />
        )}
      </div>
    </div>
  );
};

// ─── sub-component: student exam view ─────────────────────────────────────────
const StudentExamView = ({ assessment, studentName, onSubmit, onViolation }) => {
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState((assessment.durationMinutes || 30) * 60);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);
  const [submittedTotal, setSubmittedTotal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const violationsRef = useRef(0);

  // Countdown timer
  useEffect(() => {
    if (submitted) return;
    const id = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(id);
          handleSubmit(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [submitted]);

  // Tab / visibility violation detection
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && !submitted) {
        violationsRef.current += 1;
        onViolation && onViolation(violationsRef.current);
        toast.warn(`Warning ${violationsRef.current}: Switching tabs is not allowed during exam.`, { autoClose: 4000 });
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [submitted, onViolation]);

  const handleSubmit = useCallback(async (auto = false) => {
    if (submitted || submitting) return;
    setSubmitting(true);

    const total = (assessment.questions || []).reduce((s, q) => s + q.marks, 0);
    const payload = {
      studentName,
      auto,
      violations: violationsRef.current,
      answers,
    };

    try {
      const serverResult = (typeof onSubmit === "function") ? await onSubmit(payload) : null;
      const serverEarned = Number(serverResult?.earned);
      const serverTotal = Number(serverResult?.total);

      if (Number.isFinite(serverEarned)) {
        setScore(serverEarned);
        setSubmittedTotal(Number.isFinite(serverTotal) ? serverTotal : total);
      } else {
        // Local score fallback when backend returns no score payload.
        let earned = 0;
        (assessment.questions || []).forEach((q, i) => {
          const given = (answers[i] || "").trim().toLowerCase();
          const correct = (q.answer || "").trim().toLowerCase();
          if (correct && given === correct) earned += q.marks;
        });
        setScore(earned);
        setSubmittedTotal(total);
      }

      setSubmitted(true);
      if (auto) toast.info("Time up! Your answers were submitted.");
      else toast.success("Assessment submitted.");
    } catch (error) {
      toast.error(error?.message || "Unable to submit assessment.");
    } finally {
      setSubmitting(false);
    }
  }, [answers, assessment, studentName, submitted, submitting, onSubmit]);

  const mm = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const ss = String(timeLeft % 60).padStart(2, "0");
  const total = (assessment.questions || []).reduce((s, q) => s + q.marks, 0);

  const finalTotal = submittedTotal ?? total;

  if (submitted && score !== null) {
    const pct = finalTotal > 0 ? Math.round((score / finalTotal) * 100) : 0;
    return (
      <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-8 text-center">
        <FaCheckCircle className="mx-auto text-4xl text-emerald-500" />
        <p className="mt-3 text-xl font-bold text-slate-900">{assessment.title}</p>
        <p className="mt-1 text-sm text-slate-600">Submitted by {studentName}</p>
        <div className="mt-4 text-4xl font-black text-emerald-700">{score}/{finalTotal}</div>
        <div className="mt-1 text-sm font-semibold text-slate-500">{pct}%</div>
        {violationsRef.current > 0 && (
          <p className="mt-2 text-xs font-semibold text-amber-600">
            ⚠ {violationsRef.current} tab-switch violation(s) recorded
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Exam header */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div>
          <p className="text-base font-bold text-slate-900">{assessment.title}</p>
          <p className="text-xs text-slate-500">{assessment.subject} • {total} marks total</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`rounded-full px-3 py-1 text-sm font-black tabular-nums ${timeLeft < 120 ? "bg-rose-100 text-rose-700" : "bg-sky-100 text-sky-700"}`}>
            <FaClock className="mr-1 inline" />{mm}:{ss}
          </span>
          <button
            type="button"
            onClick={() => handleSubmit(false)}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            <FaStop /> {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {(assessment.questions || []).map((q, i) => {
          const cat = MARK_CATEGORIES.find((c) => c.value === q.marks) || MARK_CATEGORIES[0];
          return (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-slate-800">
                  <span className="mr-2 text-slate-400">Q{i + 1}.</span>{q.text}
                </p>
                <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${cat.color}`}>
                  [{q.marks} mark{q.marks > 1 ? "s" : ""}]
                </span>
              </div>

              {q.type === "mcq" ? (
                <div className="mt-3 space-y-2 pl-2">
                  {(q.options || []).filter(Boolean).map((opt, oi) => (
                    <label key={oi} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                      <input
                        type="radio"
                        name={`q-${i}`}
                        value={opt}
                        checked={answers[i] === opt}
                        onChange={() => setAnswers((a) => ({ ...a, [i]: opt }))}
                        className="accent-indigo-600"
                      />
                      <span className="font-medium text-slate-400 mr-1">{String.fromCharCode(65 + oi)}.</span> {opt}
                    </label>
                  ))}
                </div>
              ) : q.type === "long" ? (
                <textarea
                  rows={4}
                  value={answers[i] || ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value }))}
                  placeholder="Write your answer here…"
                  className="mt-3 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-100"
                />
              ) : (
                <input
                  type="text"
                  value={answers[i] || ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value }))}
                  placeholder="Your answer…"
                  className="mt-3 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-100"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── main exported component ──────────────────────────────────────────────────
export default function ProctoredAssessment({
  creatorRole = "teacher",
  creatorName = "Teacher",
  mode = "creator",
  studentName = "Student",
}) {
  const isStudentMode = mode === "student";
  const [assessments, setAssessments] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [view, setView] = useState("list"); // "list" | "create" | "preview" | "results"
  const [editingId, setEditingId] = useState(null);

  // form state
  const [form, setForm] = useState({
    title: "",
    subject: "",
    targetClass: "",
    durationMinutes: "30",
    questions: [],
  });
  const [filterMark, setFilterMark] = useState("all");
  const [previewAssessment, setPreviewAssessment] = useState(null);
  const [resultsAssessment, setResultsAssessment] = useState(null);
  const [studentPreviewName] = useState(studentName || "Student");

  const fetchAssessments = useCallback(async () => {
    setLoadingAssessments(true);
    try {
      const payload = await listAssessments({ role: isStudentMode ? "student" : creatorRole });
      setAssessments(Array.isArray(payload) ? payload : []);
    } catch (error) {
      toast.error(error?.message || "Unable to load assessments.");
      setAssessments([]);
    } finally {
      setLoadingAssessments(false);
    }
  }, [creatorRole, isStudentMode]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  const removeQuestion = (index) => {
    setForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== index) }));
  };

  const changeQuestion = (index, field, value) => {
    setForm((f) => {
      const next = [...f.questions];
      next[index] = { ...next[index], [field]: value };
      return { ...f, questions: next };
    });
  };

  const resetForm = () => {
    setForm({ title: "", subject: "", targetClass: "", durationMinutes: "30", questions: [] });
    setEditingId(null);
  };

  const openEdit = (asmt) => {
    setForm({
      title: asmt.title,
      subject: asmt.subject || "",
      targetClass: asmt.targetClass || "",
      durationMinutes: String(asmt.durationMinutes || 30),
      questions: asmt.questions || [],
    });
    setEditingId(asmt.id);
    setView("create");
  };

  const saveAssessment = async () => {
    if (!form.title.trim()) { toast.error("Please add an assessment title."); return; }
    if (form.questions.length === 0) { toast.error("Add at least one question."); return; }
    const incomplete = form.questions.some((q) => !q.text.trim());
    if (incomplete) { toast.error("All questions must have text."); return; }

    const payload = {
      ...form,
      durationMinutes: Number(form.durationMinutes),
      totalMarks: form.questions.reduce((total, question) => total + Number(question.marks || 0), 0),
      createdBy: creatorName,
      createdByRole: creatorRole,
    };

    if (editingId) {
      try {
        await updateAssessment(editingId, payload, { role: creatorRole });
        toast.success("Assessment updated.");
      } catch (error) {
        toast.error(error?.message || "Unable to update assessment.");
        return;
      }
    } else {
      try {
        await createAssessment(payload, { role: creatorRole });
        toast.success("Assessment created.");
      } catch (error) {
        toast.error(error?.message || "Unable to create assessment.");
        return;
      }
    }

    await fetchAssessments();
    resetForm();
    setView("list");
  };

  const publishToggle = async (id, status) => {
    try {
      if (status === "published") {
        await unpublishAssessmentApi(id, { role: creatorRole });
        toast.success("Assessment moved to draft.");
      } else {
        await publishAssessmentApi(id, { role: creatorRole });
        toast.success("Assessment published.");
      }
      await fetchAssessments();
    } catch (error) {
      toast.error(error?.message || "Unable to change assessment status.");
    }
  };

  const deleteAssessment = async (id) => {
    try {
      await deleteAssessmentApi(id, { role: creatorRole });
      toast.success("Assessment deleted.");
      await fetchAssessments();
    } catch (error) {
      toast.error(error?.message || "Unable to delete assessment.");
    }
  };

  const handleExamSubmit = async (result) => {
    if (!previewAssessment) return null;

    if (isStudentMode) {
      const response = await submitAssessmentAttempt(previewAssessment.id, result);
      const refreshed = await listAssessments({ role: "student" });
      setAssessments(Array.isArray(refreshed) ? refreshed : []);
      return {
        earned: Number(response?.earned ?? response?.score ?? 0),
        total: Number(response?.total ?? totalMarks(previewAssessment)),
      };
    }

    return {
      earned: Number(result?.earned ?? 0),
      total: Number(result?.total ?? totalMarks(previewAssessment)),
    };
  };

  // Totals per assessment
  const totalMarks = (asmt) => (asmt.questions || []).reduce((s, q) => s + (q.marks || 0), 0);

  const markSummary = (asmt) => {
    const counts = { 1: 0, 5: 0, 10: 0, 20: 0 };
    (asmt.questions || []).forEach((q) => { if (counts[q.marks] !== undefined) counts[q.marks]++; });
    return Object.entries(counts)
      .filter(([, c]) => c > 0)
      .map(([m, c]) => `${c}×${m}mk`)
      .join("  ");
  };

  const filteredQuestions = filterMark === "all"
    ? form.questions
    : form.questions.filter((q) => q.marks === Number(filterMark));
  const visibleAssessments = isStudentMode
    ? assessments.filter((item) => item.status === "published")
    : assessments;

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FaClipboardList className="text-indigo-600" />
          <h2 className="text-lg font-bold text-slate-900">Proctored Assessments</h2>
          {view !== "list" && (
            <button
              type="button"
              onClick={() => { resetForm(); setPreviewAssessment(null); setView("list"); }}
              className="ml-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              ← Back
            </button>
          )}
        </div>
        {!isStudentMode && view === "list" && (
          <button
            type="button"
            onClick={() => { resetForm(); setView("create"); }}
            className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            <FaPlus /> New assessment
          </button>
        )}
        {view === "create" && (
          <button
            type="button"
            onClick={saveAssessment}
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            <FaSave /> {editingId ? "Update" : "Save"} assessment
          </button>
        )}
      </div>

      {/* ── LIST VIEW ──────────────────────────────────────────── */}
      {view === "list" && (
        <div className="space-y-3">
          {loadingAssessments && (
            <div className="rounded-2xl border border-slate-200 bg-white px-5 py-5 text-center text-sm text-slate-500">
              Loading assessments...
            </div>
          )}
          {!loadingAssessments && visibleAssessments.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center text-sm text-slate-500">
              {isStudentMode ? "No published assessments available right now." : <>No assessments yet. Click <strong>New assessment</strong> to create one.</>}
            </div>
          )}
          {visibleAssessments.map((asmt) => {
            const isPublished = asmt.status === "published";
            return (
              <div key={asmt.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-bold text-slate-900">{asmt.title}</p>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${isPublished ? "border-emerald-200 bg-emerald-100 text-emerald-700" : "border-amber-200 bg-amber-100 text-amber-700"}`}>
                        {isPublished ? "Published" : "Draft"}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {asmt.subject} {asmt.targetClass ? `· ${asmt.targetClass}` : ""} · {asmt.durationMinutes} min · {totalMarks(asmt)} marks total
                    </p>
                    <p className="mt-0.5 text-xs text-slate-400">{markSummary(asmt)}</p>
                    {asmt.results?.length > 0 && (
                      <p className="mt-0.5 text-xs text-indigo-500">{asmt.results.length} submission(s)</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {isStudentMode ? (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await startAssessmentAttempt(asmt.id);
                            setPreviewAssessment(asmt);
                            setView("preview");
                          } catch (error) {
                            toast.error(error?.message || "Unable to start assessment.");
                          }
                        }}
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                      >
                        <FaPlay size={10} /> Start assessment
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => openEdit(asmt)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                        >
                          <FaEdit size={10} /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => { setPreviewAssessment(asmt); setView("preview"); }}
                          className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-100"
                        >
                          <FaEye size={10} /> Preview
                        </button>
                        <button
                          type="button"
                          onClick={() => publishToggle(asmt.id, asmt.status)}
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition ${isPublished ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100" : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
                        >
                          {isPublished ? <><FaLock size={10} /> Unpublish</> : <><FaUnlock size={10} /> Publish</>}
                        </button>
                        {asmt.results?.length > 0 && (
                          <button
                            type="button"
                            onClick={() => { setResultsAssessment(asmt); setView("results"); }}
                            className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                          >
                            <FaList size={10} /> Results
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteAssessment(asmt.id)}
                          className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-100"
                        >
                          <FaTrash size={10} /> Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── CREATE / EDIT VIEW ─────────────────────────────────── */}
      {!isStudentMode && view === "create" && (
        <div className="space-y-4">
          {/* Assessment meta */}
          <div className="rounded-2xl border border-indigo-100 bg-white p-5 shadow-sm">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-indigo-500">Assessment details</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Assessment title (e.g., Chapter 5 Test)"
                className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
              <input
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder="Subject (e.g., Mathematics)"
                className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
              <input
                value={form.targetClass}
                onChange={(e) => setForm((f) => ({ ...f, targetClass: e.target.value }))}
                placeholder="Target class (e.g., Class 8A)"
                className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
              <div className="flex items-center gap-2">
                <FaClock className="shrink-0 text-slate-400" />
                <input
                  type="number"
                  min={5}
                  max={180}
                  value={form.durationMinutes}
                  onChange={(e) => setForm((f) => ({ ...f, durationMinutes: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                />
                <span className="shrink-0 text-xs text-slate-500">min</span>
              </div>
            </div>
          </div>

          {/* Mark filter */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Filter by:</span>
            {[{ value: "all", label: "All" }, ...MARK_CATEGORIES].map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setFilterMark(String(c.value))}
                className={`rounded-full border px-3 py-0.5 text-xs font-semibold transition ${
                  filterMark === String(c.value)
                    ? "border-indigo-300 bg-indigo-100 text-indigo-700"
                    : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                }`}
              >
                {c.label}
              </button>
            ))}
            <span className="ml-auto text-xs text-slate-400">
              {form.questions.length} question(s) · {form.questions.reduce((s, q) => s + q.marks, 0)} marks
            </span>
          </div>

          {/* Questions list */}
          <div className="space-y-3">
            {filteredQuestions.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-400">
                {filterMark === "all" ? "No questions yet. Click Add question." : `No ${filterMark}-mark questions.`}
              </div>
            )}
            {form.questions.map((q, i) => {
              if (filterMark !== "all" && q.marks !== Number(filterMark)) return null;
              return (
                <QuestionEditor
                  key={i}
                  index={i}
                  question={q}
                  onChange={changeQuestion}
                  onRemove={removeQuestion}
                />
              );
            })}
          </div>

          {/* Add question buttons */}
          <div className="flex flex-wrap gap-2">
            {MARK_CATEGORIES.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => {
                  setForm((f) => ({ ...f, questions: [...f.questions, { ...DEFAULT_QUESTION, marks: c.value }] }));
                  setFilterMark("all");
                }}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition hover:opacity-80 ${c.color}`}
              >
                <FaPlus size={9} /> {c.label} question
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── PREVIEW / EXAM VIEW ────────────────────────────────── */}
      {view === "preview" && previewAssessment && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <FaExclamationTriangle />
            <span>
              <strong>{isStudentMode ? "Assessment mode" : "Proctor preview"}</strong> — Tab-switch violations are tracked during assessment.
            </span>
          </div>
          <StudentExamView
            assessment={previewAssessment}
            studentName={studentPreviewName}
            onSubmit={handleExamSubmit}
            onViolation={() => {}}
          />
        </div>
      )}

      {/* ── RESULTS VIEW ───────────────────────────────────────── */}
      {view === "results" && resultsAssessment && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-slate-700">
            Results — <span className="font-bold text-slate-900">{resultsAssessment.title}</span>
          </p>
          {(resultsAssessment.results || []).length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-400">
              No submissions yet.
            </div>
          )}
          {(resultsAssessment.results || []).map((r, i) => {
            const pct = r.total > 0 ? Math.round((r.earned / r.total) * 100) : 0;
            return (
              <div key={i} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{r.studentName}</p>
                  <p className="text-xs text-slate-500">{new Date(r.submittedAt).toLocaleString()}</p>
                  {r.violations > 0 && (
                    <p className="text-xs font-semibold text-amber-600">⚠ {r.violations} violation(s)</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-lg font-black text-slate-900">{r.earned}/{r.total}</p>
                  <p className="text-xs font-semibold text-slate-500">{pct}%</p>
                  {r.auto && <p className="text-[10px] text-rose-500">Auto-submitted (time up)</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
